import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS: Record<string, { label: string; cls: string }> = {
  niet_gevonden: { label: 'Niet gevonden', cls: 'bg-red-100 text-red-700' },
  aangevraagd: { label: 'Aangevraagd', cls: 'bg-amber-100 text-amber-700' },
  onbekend: { label: 'Onbekend', cls: 'bg-slate-100 text-slate-600' },
};

export const AdminAllianzMatchingPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState<Record<string, { eh: string; grade: string }>>({});
  const [busy, setBusy] = useState('');
  const notify = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/allianz-matching`, { headers });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const koppel = async (id: string) => {
    const v = vals[id] || { eh: '', grade: '' };
    if (!v.eh) return;
    setBusy(id);
    try {
      const body: any = { euler_hermes_id: v.eh };
      if (v.grade) body.grade = Number(v.grade);
      const r = await fetch(`${API_BASE_URL}/api/admin/debtors/${id}/euler-hermes`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      notify.success('De debiteur is gekoppeld aan Allianz Trade en het lid is geïnformeerd.', 'Debiteur gekoppeld');
      load();
    } catch { notify.error('Koppelen mislukt.'); } finally { setBusy(''); }
  };

  return (
    <Layout>
      <p className="text-sm text-slate-500 mb-6">
        Debiteuren die leden hebben opgevoerd maar (nog) niet in de Allianz Trade-database staan.
        Koppel het Allianz Trade-nummer (EulerHermes-ID) zodat de kredietcheck kan worden uitgevoerd.
      </p>
      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          Geen openstaande matches. Nieuwe niet-gevonden debiteuren verschijnen hier automatisch.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Debiteur', 'Land', 'Lid', 'Status', 'EulerHermes-ID koppelen', ''].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((d) => {
                const st = STATUS[d.match_status] || STATUS.onbekend;
                const v = vals[d.id] || { eh: '', grade: '' };
                return (
                  <tr key={d.id} className="hover:bg-slate-50 align-top">
                    <td className="px-5 py-3"><div className="font-medium text-mfb-blue">{d.company_name}</div><div className="text-xs text-slate-400">{d.company_number || '—'}</div></td>
                    <td className="px-5 py-3 text-slate-700">{d.country_code}</td>
                    <td className="px-5 py-3 text-slate-700">{d.lid}<div className="text-xs text-slate-400">{d.collectiviteit}</div></td>
                    <td className="px-5 py-3"><span className={`px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <input placeholder="EH-ID" value={v.eh} onChange={(e) => setVals({ ...vals, [d.id]: { ...v, eh: e.target.value } })}
                          className="w-32 px-2 py-1 border border-slate-300 rounded text-sm" />
                        <input placeholder="grade" type="number" value={v.grade} onChange={(e) => setVals({ ...vals, [d.id]: { ...v, grade: e.target.value } })}
                          className="w-16 px-2 py-1 border border-slate-300 rounded text-sm" />
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <button disabled={busy === d.id || !v.eh} onClick={() => koppel(d.id)}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-mfb-blue hover:bg-mfb-blue-dark rounded-lg disabled:opacity-50">
                        Koppelen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

export default AdminAllianzMatchingPage;
